import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { mergeSavedSections } from '../utils/seasonReportSections';

const API = process.env.REACT_APP_BACKEND_URL;

const SeasonReportPrint = ({ data: dataProp = null, embedded = false }) => {
  const [data, setData] = useState(dataProp);
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('id');
  const shared = searchParams.get('shared');

  useEffect(() => {
    // Phase 76.3 — when used embedded (inside SeasonReportView), the parent
    // supplies the loaded snapshot directly. Skip the network fetch.
    if (embedded && dataProp) { setData(dataProp); return; }
    const loadData = async () => {
      // Phase 77 — helper: ensure the data shape always has a fully-merged
      // sections dict so legacy snapshots keep rendering the same blocks.
      const withSections = (d) => ({ ...d, sections: mergeSavedSections(d?.sections) });
      // Priority: shared link > saved report > localStorage
      if (shared) {
        try {
          const res = await fetch(`${API}/api/reports/season/shared/${shared}`);
          if (res.ok) {
            const report = await res.json();
            setData(withSections({ ...report.snapshot, coach_input: report.coach_input, season: report.season, generated_at: report.created_at, report_id: report.id }));
            return;
          }
        } catch { /* fallthrough */ }
      }
      if (reportId) {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API}/api/reports/season/saved/${reportId}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const report = await res.json();
            setData(withSections({ ...report.snapshot, coach_input: report.coach_input, season: report.season, generated_at: report.created_at, report_id: report.id }));
            return;
          }
        } catch { /* fallthrough */ }
      }
      // Fallback: localStorage
      const stored = localStorage.getItem('pfap_season_report');
      if (stored) { try { setData(withSections(JSON.parse(stored))); } catch { /* invalid */ } }
    };
    loadData();
  }, [reportId, shared, embedded, dataProp]);

  if (!data) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Poppins, sans-serif' }}>
        <p style={{ color: '#6b7280', fontSize: 14 }}>No report data found. Please generate from the builder.</p>
      </div>
    );
  }

  const a = data.athlete || {};
  const p = data.performance || {};
  const att = data.attendance || {};
  const coach = data.coach_input || {};
  const pdp = data.pdp || [];
  const flags = data.flags || [];

  const formatDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  const handlePrint = () => window.print();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Poppins:wght@300;400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #f8f9fa; }
        @media print {
          body { background: white; }
          .print-bar { display: none !important; }
          .report-page { box-shadow: none !important; margin: 0 !important; max-width: none !important; }
          @page { size: A4; margin: 12mm 15mm; }
          .section { break-inside: avoid; }
        }
      `}</style>

      {/* Print Bar */}
      <div className="print-bar" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, background: '#303f24', padding: '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ color: '#c6cd94', fontSize: 13, fontFamily: 'Poppins, sans-serif' }}>PFAP Hub — Season Report Preview</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {data.report_id && (
            <button
              onClick={() => {
                const url = `${window.location.origin}/season-report/print?shared=${data.report_id}`;
                navigator.clipboard.writeText(url);
                const btn = document.getElementById('share-btn');
                if (btn) { btn.textContent = 'Link Copied!'; setTimeout(() => { btn.textContent = 'Copy Share Link'; }, 2000); }
              }}
              id="share-btn"
              style={{ background: 'transparent', color: '#c6cd94', border: '1px solid #c6cd94', padding: '6px 16px', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'Poppins, sans-serif' }}
              data-testid="share-report-btn"
            >
              Copy Share Link
            </button>
          )}
          <button
            onClick={handlePrint}
            style={{ background: '#c6cd94', color: '#303f24', border: 'none', padding: '6px 20px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Poppins, sans-serif' }}
            data-testid="print-report-btn"
          >
            Download / Print Report
          </button>
        </div>
      </div>

      {/* Report Page */}
      <div className="report-page" style={{ maxWidth: 800, margin: '64px auto 40px', background: 'white', fontFamily: 'Poppins, sans-serif', color: '#1f2937', lineHeight: 1.6, padding: '40px 48px', boxShadow: '0 1px 8px rgba(0,0,0,.08)' }} data-testid="season-report-print">

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #303f24', paddingBottom: 16, marginBottom: 24 }}>
          <div>
            <img src="/pfap-logo-black.png" alt="PFAP" style={{ height: 40, marginBottom: 4 }} onError={(e) => { e.target.style.display = 'none'; }} />
            <div style={{ fontSize: 10, color: '#6b7280', letterSpacing: '0.05em' }}>Pro-Football Academy Portugal</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: 'Anton, sans-serif', fontSize: 20, color: '#303f24', textTransform: 'uppercase', letterSpacing: '0.03em' }}>End of Season Report</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Season {data.season}</div>
            {data.status && (
              /* Phase 76.3 — status badge inline with the title so it is
                 always visible on both screen and print. */
              <div style={{ marginTop: 6 }} data-testid="print-status-badge">
                <span style={{
                  display: 'inline-block', padding: '2px 8px', fontSize: 10,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  borderRadius: 999,
                  border: '1px solid',
                  ...(data.status === 'finalized'
                    ? { background: '#ecfdf5', color: '#065f46', borderColor: '#a7f3d0' }
                    : data.status === 'archived'
                      ? { background: '#f3f4f6', color: '#374151', borderColor: '#e5e7eb' }
                      : { background: '#fef3c7', color: '#92400e', borderColor: '#fde68a' }),
                }}>{data.status}</span>
              </div>
            )}
          </div>
        </div>

        {/* Phase 76.3 — Draft warning banner so parents/players never see a
            draft accidentally (in addition to the server-side 404 for them). */}
        {(data.status === 'draft') && (
          <div
            data-testid="print-draft-banner"
            style={{
              fontSize: 11, padding: '8px 12px', marginBottom: 16,
              background: '#fef3c7', border: '1px solid #fde68a',
              borderRadius: 6, color: '#92400e',
            }}
          >
            Draft — not visible to parents/players.
          </div>
        )}

        {/* A. Athlete Overview */}
        <Section title="Athlete Overview">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 32px' }}>
            <Field label="Full Name" value={a.full_name} />
            <Field label="Position" value={a.position} />
            <Field label="Date of Birth" value={formatDate(a.date_of_birth)} />
            <Field label="Nationality" value={a.nationality} />
            <Field label="Squad" value={a.squad} />
            <Field label="Season" value={data.season} />
          </div>
        </Section>

        {/* Phase 77 — Executive Summary (new narrative section). */}
        {data.sections?.executive_summary && (
          <Section title="Executive Summary">
            {data.coach_input?.executive_summary ? (
              <div
                className="season-report-rich"
                style={{ fontSize: 12, color: '#374151', lineHeight: 1.55 }}
                data-testid="print-executive-summary"
                dangerouslySetInnerHTML={{ __html: data.coach_input.executive_summary }}
              />
            ) : <EmptyText>Not provided.</EmptyText>}
          </Section>
        )}

        {/* Phase 76.2 — Season Highlights, placed near the top so readers see
            the headline outcomes before drilling into module detail. */}
        {data.sections?.season_highlights && Array.isArray(data.coach_input?.season_highlights) && data.coach_input.season_highlights.length > 0 && (
          <Section title="Season Highlights">
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#303f24', lineHeight: 1.6 }}
                className="season-report-rich"
                data-testid="print-season-highlights">
              {data.coach_input.season_highlights.map((h, i) => (
                <li key={i} style={{ marginBottom: 4 }}
                    dangerouslySetInnerHTML={{ __html: String(h) }} />
              ))}
            </ul>
          </Section>
        )}

        {/* B. Performance Summary (Match Reports toggle) */}
        {data.sections?.match_reports && (
        <Section title="Performance Summary">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
            <StatBox label="Matches Played" value={p.total_matches ?? 0} />
            <StatBox label="Minutes Played" value={p.total_minutes ?? 0} />
            <StatBox label="Average Rating" value={p.avg_rating ? `${p.avg_rating}/10` : '-'} accent />
            <StatBox label="Goals" value={p.goals ?? 0} />
            <StatBox label="Assists" value={p.assists ?? 0} />
            <StatBox label="Clean Sheets" value="-" />
          </div>
          {p.recent_matches?.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <Th>Date</Th><Th>Opponent</Th><Th>Competition</Th><Th>Position</Th><Th>Mins</Th><Th>Rating</Th>
                </tr>
              </thead>
              <tbody>
                {p.recent_matches.map((m, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <Td>{m.date}</Td>
                    <Td>{m.opponent}</Td>
                    <Td>{m.competition || '-'}</Td>
                    <Td>{m.position || '-'}</Td>
                    <Td>{m.minutes || '-'}</Td>
                    <Td>{m.rating ? `${m.rating}/10` : '-'}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
        )}

        {/* Phase 77 — Match Statistics (per-category averages from match
            evaluations). Renders only when the toggle is on AND the
            backend computed at least one category. */}
        {data.sections?.match_statistics && p.avg_metrics && Object.keys(p.avg_metrics).length > 0 && (
          <Section title="Match Statistics">
            <div data-testid="print-match-statistics" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
              {Object.entries(p.avg_metrics).map(([cat, metrics]) => (
                <div key={cat} style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'capitalize', color: '#303f24', marginBottom: 6 }}>{cat}</div>
                  {Object.entries(metrics).map(([m, v]) => (
                    <div key={m} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#4b5563', marginBottom: 2 }}>
                      <span style={{ textTransform: 'capitalize' }}>{m.replace(/_/g, ' ')}</span>
                      <span style={{ fontWeight: 500 }}>{v}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* C. PDP Summary */}
        {data.sections?.pdp && (
        <Section title="Player Development Plan (PDP)">
          {pdp.length > 0 ? pdp.map((cycle, i) => (
            <div key={i} style={{ marginBottom: i < pdp.length - 1 ? 16 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#303f24' }}>{cycle.cycle_name}</span>
                <Tag text={cycle.status} color={cycle.status === 'active' ? '#059669' : '#6b7280'} />
                <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 'auto' }}>{cycle.overall_progress}% overall</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', fontSize: 11, color: '#4b5563', marginBottom: 8 }}>
                <span>Total Goals: {cycle.total_goals}</span>
                <span>Completed: {cycle.completed_goals}</span>
              </div>
              {Object.entries(cycle.categories || {}).map(([cat, goals]) => {
                const avg = Math.round(goals.reduce((s, g) => s + g.progress, 0) / goals.length);
                return (
                  <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: '#6b7280', width: 80, textTransform: 'capitalize' }}>{cat}</span>
                    <div style={{ flex: 1, height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: '#7c3aed', borderRadius: 3, width: `${avg}%` }} />
                    </div>
                    <span style={{ fontSize: 10, color: '#9ca3af', width: 30, textAlign: 'right' }}>{avg}%</span>
                  </div>
                );
              })}
            </div>
          )) : <EmptyText>No PDP data available for this season.</EmptyText>}
        </Section>
        )}

        {/* D. Attendance Summary — Phase 74.1 unified breakdown */}
        {data.sections?.attendance && (
        <Section title="Attendance Summary">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            <StatBox
              label="Academy Sessions in Period"
              value={att.academy_sessions_in_period ?? att.total_sessions ?? 0}
            />
            <StatBox
              label="Assigned / Invited"
              value={att.assigned_sessions ?? att.total_sessions ?? 0}
            />
            <StatBox
              label="Attended"
              value={att.attended_or_justified ?? att.sessions_attended ?? 0}
            />
            <StatBox
              label="Attendance %"
              value={Number.isFinite(att.attendance_rate ?? att.attendance_pct) ? `${att.attendance_rate ?? att.attendance_pct}%` : '—'}
              accent
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 8 }}>
            <StatBox label="Not Attended" value={att.absent_or_no_show ?? att.sessions_absent ?? 0} />
            <StatBox label="Pending / Unmarked" value={att.pending_or_unmarked ?? 0} />
            <StatBox label="Marked Sessions" value={att.marked_sessions ?? 0} />
            <StatBox label="Present" value={att.status_breakdown?.Present ?? 0} />
            <StatBox label="Full Training Availability" value={Number.isFinite(att.availability_rate) ? `${att.availability_rate}%` : '—'} />
          </div>
          <p style={{ marginTop: 8, fontSize: 10, color: '#6b7280' }}>
            Academy total ({att.academy_sessions_in_period ?? 0}) counts all training sessions in the season.
            Athlete-specific stats use only the {att.assigned_sessions ?? 0} session(s) this athlete was assigned/invited to.
            Pending entries are not counted as Absent.
          </p>
        </Section>
        )}

        {/* E. Flags / Discipline (always-on — discipline info has no toggle yet) */}
        <Section title="Flags / Discipline">
          {flags.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <Th>Type</Th><Th>Severity</Th><Th>Status</Th><Th>Description</Th>
                </tr>
              </thead>
              <tbody>
                {flags.map((f, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <Td>{f.flag_type}</Td>
                    <Td><SeverityDot severity={f.severity} /> {f.severity}</Td>
                    <Td>{f.status}</Td>
                    <Td style={{ maxWidth: 300 }}>{f.description}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyText>No disciplinary flags recorded this season.</EmptyText>}
        </Section>

        {/* ─── Phase 76.1 — Extended Module Sections (Batch A) ─────────────
            Each block renders only when the corresponding `sections.<key>`
            toggle is ON in the builder AND the backend returned a payload
            for that module. Missing data falls back to "No records available
            for this season." per spec. */}
        {data.sections?.fitness && (
          <Section title="Fitness Assessment Summary">
            <FitnessAssessmentBlock fa={data.extended?.fitness_assessment} />
            <StaffSummary text={data.coach_input?.section_summaries?.fitness} testid="print-summary-fitness" />
          </Section>
        )}

        {data.sections?.biometrics && (
          <Section title="Biometrics Summary">
            <BiometricsBlock bm={data.extended?.biometrics} />
            <StaffSummary text={data.coach_input?.section_summaries?.biometrics} testid="print-summary-biometrics" />
          </Section>
        )}

        {data.sections?.nutrition && (
          <Section title="Nutrition Summary">
            <NutritionBlock n={data.extended?.nutrition} />
            <StaffSummary text={data.coach_input?.section_summaries?.nutrition} testid="print-summary-nutrition" />
          </Section>
        )}

        {data.sections?.mental && (
          <Section title="Mental Development Summary">
            <MentalBlock m={data.extended?.mental_hub} />
            <StaffSummary text={data.coach_input?.section_summaries?.mental} testid="print-summary-mental" />
          </Section>
        )}

        {data.sections?.academic && (
          <Section title="Academic Summary">
            <AcademicBlock ac={data.extended?.academic} />
            <StaffSummary text={data.coach_input?.section_summaries?.academic} testid="print-summary-academic" />
          </Section>
        )}

        {data.sections?.injury && (
          <Section title="Injury & Recovery Summary">
            <InjuryBlock i={data.extended?.injury_recovery} />
            <StaffSummary text={data.coach_input?.section_summaries?.injury} testid="print-summary-injury" />
          </Section>
        )}

        {data.sections?.career && (
          <Section title="Career Pathway Summary">
            <CareerBlock c={data.extended?.career_pathway} />
            <StaffSummary text={data.coach_input?.section_summaries?.career} testid="print-summary-career" />
          </Section>
        )}

        {/* F. Strengths (Areas of Strength toggle) */}
        {data.sections?.strengths && (
        <Section title="Strengths">
          {coach.strengths ? (
            <div
              className="season-report-rich"
              style={{ fontSize: 12, color: '#374151', lineHeight: 1.55 }}
              data-testid="print-coach-strengths"
              dangerouslySetInnerHTML={{ __html: coach.strengths }}
            />
          ) : <EmptyText>Not provided.</EmptyText>}
        </Section>
        )}

        {/* G. Areas for Improvement (Areas to Improve toggle) */}
        {data.sections?.improvements && (
        <Section title="Areas for Improvement">
          {coach.improvements ? (
            <div
              className="season-report-rich"
              style={{ fontSize: 12, color: '#374151', lineHeight: 1.55 }}
              data-testid="print-coach-improvements"
              dangerouslySetInnerHTML={{ __html: coach.improvements }}
            />
          ) : <EmptyText>Not provided.</EmptyText>}
        </Section>
        )}

        {/* H. Coach Feedback (Coach Feedback toggle — text from `evaluation`) */}
        {data.sections?.coach_feedback && (
        <Section title="Coach Feedback">
          {coach.rating && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 11, color: '#6b7280' }}>Overall Rating:</span>
              <span style={{ fontSize: 22, fontWeight: 700, color: coach.rating >= 7 ? '#059669' : coach.rating >= 5 ? '#d97706' : '#dc2626' }}>{coach.rating}/10</span>
            </div>
          )}
          {coach.evaluation ? (
            <div
              className="season-report-rich"
              style={{ fontSize: 12, color: '#374151', lineHeight: 1.55 }}
              data-testid="print-coach-evaluation"
              dangerouslySetInnerHTML={{ __html: coach.evaluation }}
            />
          ) : <EmptyText>No evaluation comments provided.</EmptyText>}
        </Section>
        )}

        {/* Phase 77 — Recommendations (new narrative section). */}
        {data.sections?.recommendations && (
          <Section title="Recommendations">
            {coach.recommendations ? (
              <div
                className="season-report-rich"
                style={{ fontSize: 12, color: '#374151', lineHeight: 1.55 }}
                data-testid="print-recommendations"
                dangerouslySetInnerHTML={{ __html: coach.recommendations }}
              />
            ) : <EmptyText>Not provided.</EmptyText>}
          </Section>
        )}

        {/* Phase 77 — Next Season Action Plan (new narrative section). */}
        {data.sections?.next_season_plan && (
          <Section title="Next Season Action Plan">
            {coach.next_season_plan ? (
              <div
                className="season-report-rich"
                style={{ fontSize: 12, color: '#374151', lineHeight: 1.55 }}
                data-testid="print-next-season-plan"
                dangerouslySetInnerHTML={{ __html: coach.next_season_plan }}
              />
            ) : <EmptyText>Not provided.</EmptyText>}
          </Section>
        )}

        {/* Footer */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 9, color: '#9ca3af' }}>
            Generated: {formatDate(data.generated_at)} | Pro-Football Academy Portugal (PFAP) — Confidential
          </div>
          <img src="/pfap-logo-black.png" alt="" style={{ height: 20, opacity: 0.3 }} onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
      </div>
    </>
  );
};

/* ============ Sub-components with inline styles for print compatibility ============ */

const Section = ({ title, children }) => (
  <div className="section" style={{ marginBottom: 24 }}>
    <div style={{ fontSize: 14, fontWeight: 700, color: '#303f24', borderBottom: '1px solid #c6cd94', paddingBottom: 6, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {title}
    </div>
    {children}
  </div>
);

const Field = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 8, padding: '4px 0' }}>
    <span style={{ fontSize: 11, color: '#6b7280', minWidth: 100 }}>{label}:</span>
    <span style={{ fontSize: 11, fontWeight: 500, color: '#1f2937' }}>{value || '-'}</span>
  </div>
);

const StatBox = ({ label, value, accent }) => (
  <div style={{ textAlign: 'center', padding: '10px 8px', background: '#f9fafb', borderRadius: 6, border: '1px solid #f3f4f6' }}>
    <div style={{ fontSize: 20, fontWeight: 700, color: accent ? '#303f24' : '#1f2937' }}>{value}</div>
    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{label}</div>
  </div>
);

const Tag = ({ text, color }) => (
  <span style={{ fontSize: 10, fontWeight: 500, color, background: `${color}15`, padding: '1px 8px', borderRadius: 10, textTransform: 'capitalize' }}>{text}</span>
);

const SeverityDot = ({ severity }) => {
  const colors = { High: '#dc2626', Medium: '#d97706', Low: '#3b82f6' };
  return <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: colors[severity] || '#9ca3af', marginRight: 4, verticalAlign: 'middle' }} />;
};

const Th = ({ children }) => (
  <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 10, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{children}</th>
);

const Td = ({ children, style = {} }) => (
  <td style={{ padding: '6px 8px', fontSize: 11, color: '#374151', ...style }}>{children}</td>
);

const EmptyText = ({ children }) => (
  <p style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>{children}</p>
);

/* ───────── Phase 76.1 — Extended module blocks (Batch A) ─────────
   Each block accepts the section payload returned by the backend and
   gracefully degrades to "No records available for this season." when
   the payload is missing/empty. Inline styles only — keeps everything
   printable identically across browsers. */

const NoData = () => <EmptyText>No records available for this season.</EmptyText>;

/** Phase 76.2/76.3 — staff-authored rich text appended to each module section. */
const StaffSummary = ({ text, testid }) => {
  if (!text || !String(text).trim()) return null;
  return (
    <div
      data-testid={testid}
      className="season-report-rich"
      style={{
        marginTop: 12,
        padding: 10,
        borderLeft: '3px solid #c6cd94',
        background: '#f9fafb',
        fontSize: 11.5,
        lineHeight: 1.55,
        color: '#1f2937',
      }}
    >
      <p style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
        Staff Summary
      </p>
      {/* Phase 76.3 — rich HTML, sanitised on save by backend bleach. */}
      <div dangerouslySetInnerHTML={{ __html: String(text) }} />
    </div>
  );
};

const FitnessAssessmentBlock = ({ fa }) => {
  if (!fa || !fa.sessions_count) return <NoData />;
  return (
    <div data-testid="print-fitness">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
        <StatBox label="Sessions" value={fa.sessions_count} />
        <StatBox label="First Session" value={fa.first?.session_date || '-'} />
        <StatBox label="Latest Session" value={fa.latest?.session_date || '-'} accent />
      </div>
      {fa.key_changes?.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead><tr style={{ borderBottom: '1px solid #e5e7eb' }}>
            <Th>Test</Th><Th>Category</Th><Th>First</Th><Th>Latest</Th><Th>Δ</Th><Th>Standard</Th>
          </tr></thead>
          <tbody>
            {fa.key_changes.map((k, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <Td>{k.test_type}</Td>
                <Td>{k.category || '-'}</Td>
                <Td>{k.first_value ?? '-'}</Td>
                <Td>{k.latest_value ?? '-'}</Td>
                <Td>{k.delta !== null && k.delta !== undefined ? (k.delta > 0 ? `+${k.delta}` : k.delta) : '-'}</Td>
                <Td>{k.latest_label || '-'}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {fa.needs_attention?.length > 0 && (
        <p style={{ marginTop: 10, fontSize: 11, color: '#b45309' }}>
          <strong>Needs attention:</strong> {fa.needs_attention.map(n => n.test_type).join(', ')}
        </p>
      )}
    </div>
  );
};

const BiometricsBlock = ({ bm }) => {
  if (!bm || !bm.entries_count) return <NoData />;
  const rows = [
    ['Weight (kg)', 'weight_kg'],
    ['Height (cm)', 'height_cm'],
    ['Body Fat %', 'fat_mass_pct'],
    ['Muscle Mass %', 'muscle_mass_pct'],
    ['BMI', 'bmi'],
    ['Σ Skinfolds (mm)', 'sum_of_skinfolds_mm'],
    ['Waist (cm)', 'waist_circumference_cm'],
  ];
  const fmt = (v) => (v === null || v === undefined ? '-' : v);
  return (
    <div data-testid="print-biometrics">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
        <StatBox label="Entries" value={bm.entries_count} />
        <StatBox label="First Entry" value={bm.first?.date || '-'} />
        <StatBox label="Latest Entry" value={bm.latest?.date || '-'} accent />
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead><tr style={{ borderBottom: '1px solid #e5e7eb' }}>
          <Th>Metric</Th><Th>Start of Season</Th><Th>Latest</Th><Th>Change</Th>
        </tr></thead>
        <tbody>
          {rows.map(([label, key]) => (
            <tr key={key} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <Td>{label}</Td>
              <Td>{fmt(bm.first?.[key])}</Td>
              <Td>{fmt(bm.latest?.[key])}</Td>
              <Td>{bm.deltas?.[key] !== null && bm.deltas?.[key] !== undefined ? (bm.deltas[key] > 0 ? `+${bm.deltas[key]}` : bm.deltas[key]) : '-'}</Td>
            </tr>
          ))}
        </tbody>
      </table>
      {bm.latest_notes && (
        <p style={{ marginTop: 10, fontSize: 11, color: '#374151' }}><strong>Staff notes:</strong> {bm.latest_notes}</p>
      )}
    </div>
  );
};

const NutritionPlanBlock = ({ p, current }) => (
  <div style={{ marginBottom: 12, padding: 10, border: '1px solid #e5e7eb', borderRadius: 6, background: current ? '#f9fafb' : 'white' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#303f24' }}>{p.title}</span>
      {current && <Tag text="Active" color="#059669" />}
      <span style={{ fontSize: 10, color: '#6b7280' }}>{p.plan_type}</span>
    </div>
    {p.description && <p style={{ fontSize: 11, color: '#4b5563', marginBottom: 4 }}>{p.description}</p>}
    {p.meals && <p style={{ fontSize: 10, color: '#374151' }}><strong>Meals:</strong> {p.meals}</p>}
    {p.hydration && <p style={{ fontSize: 10, color: '#374151' }}><strong>Hydration:</strong> {p.hydration}</p>}
    {p.supplements && <p style={{ fontSize: 10, color: '#374151' }}><strong>Supplements:</strong> {p.supplements}</p>}
    {p.coaching_notes && <p style={{ fontSize: 10, color: '#374151' }}><strong>Notes:</strong> {p.coaching_notes}</p>}
  </div>
);

const NutritionBlock = ({ n }) => {
  if (!n || (!n.active_plan && !n.older_plans?.length)) return <NoData />;
  return (
    <div data-testid="print-nutrition">
      {n.active_plan && <NutritionPlanBlock p={n.active_plan} current />}
      {n.older_plans?.map((p, i) => <NutritionPlanBlock key={i} p={p} />)}
    </div>
  );
};

const MentalBlock = ({ m }) => {
  if (!m || (!m.entries_count && !m.responses_count)) return <NoData />;
  return (
    <div data-testid="print-mental">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
        <StatBox label="Entries" value={m.entries_count} />
        <StatBox label="Responses" value={m.responses_count} />
        <StatBox label="Completed Tasks" value={m.completed_tasks} accent />
      </div>
      {m.themes?.length > 0 && (
        <p style={{ fontSize: 11, color: '#374151' }}>
          <strong>Themes covered:</strong>{' '}
          {m.themes.map((t, i) => (
            <span key={i} style={{ marginRight: 6 }}>{t.label} ({t.count})</span>
          ))}
        </p>
      )}
      <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 8, fontStyle: 'italic' }}>
        Privacy: only counts and entry-type themes are surfaced. Free-text private notes are not included by default.
      </p>
    </div>
  );
};

const AcademicBlock = ({ ac }) => {
  if (!ac || (!ac.profiles?.length && !ac.reports_count && !ac.attendance?.weeks)) return <NoData />;
  const lr = ac.latest_report;
  const att = ac.attendance || {};
  return (
    <div data-testid="print-academic">
      {ac.profiles?.length > 0 && (
        <p style={{ fontSize: 11, color: '#374151', marginBottom: 8 }}>
          <strong>Programmes:</strong> {ac.profiles.map(p => p.profile_name).filter(Boolean).join(', ')}
        </p>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
        <StatBox label="Reports" value={ac.reports_count} />
        <StatBox label="Weeks Tracked" value={att.weeks || 0} />
        <StatBox label="Attendance %" value={att.rate !== null && att.rate !== undefined ? `${att.rate}%` : '-'} accent />
      </div>
      {lr && (
        <div style={{ padding: 10, border: '1px solid #e5e7eb', borderRadius: 6 }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: '#303f24', marginBottom: 4 }}>
            Latest Report — {lr.period || 'Untitled period'}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 24px', fontSize: 11, color: '#4b5563', marginBottom: 4 }}>
            <span>Average Grade: <strong>{lr.average_grade || '-'}</strong></span>
            {lr.average_course_completion_percentage !== null && lr.average_course_completion_percentage !== undefined && (
              <span>Avg Completion: <strong>{lr.average_course_completion_percentage}%</strong></span>
            )}
            <span>Subjects: {lr.subjects_count}</span>
          </div>
          {lr.teacher_comment && <p style={{ fontSize: 11, color: '#374151' }}><strong>Teacher:</strong> {lr.teacher_comment}</p>}
          {lr.academy_comment && <p style={{ fontSize: 11, color: '#374151' }}><strong>Academy:</strong> {lr.academy_comment}</p>}
        </div>
      )}
    </div>
  );
};

const InjuryBlock = ({ i }) => {
  if (!i || !i.injuries_count) return <NoData />;
  return (
    <div data-testid="print-injury">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 12 }}>
        <StatBox label="Total Injuries" value={i.injuries_count} />
        <StatBox label="Currently Active" value={i.active_count} accent />
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead><tr style={{ borderBottom: '1px solid #e5e7eb' }}>
          <Th>Injury</Th><Th>Body Area</Th><Th>Severity</Th><Th>Status</Th><Th>Recovery %</Th><Th>RTP</Th>
        </tr></thead>
        <tbody>
          {i.items?.map((row, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <Td>{row.injury_type || '-'}</Td>
              <Td>{row.body_area || '-'}</Td>
              <Td>{row.severity || '-'}</Td>
              <Td>{row.status || '-'}</Td>
              <Td>{row.latest_recovery_percent !== null && row.latest_recovery_percent !== undefined ? `${row.latest_recovery_percent}%` : '-'}</Td>
              <Td>{row.clearance_status || 'Not Cleared'}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CareerBlock = ({ c }) => {
  if (!c || !c.phases_count) return <NoData />;
  const cp = c.current_phase || {};
  return (
    <div data-testid="print-career">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
        <StatBox label="Total Phases" value={c.phases_count} />
        <StatBox label="Current Phase" value={cp.phase_name || '-'} accent />
        <StatBox label="Progress" value={cp.completion_percentage !== null && cp.completion_percentage !== undefined ? `${cp.completion_percentage}%` : '-'} />
      </div>
      {c.completed_milestones?.length > 0 && (
        <p style={{ fontSize: 11, color: '#374151', marginBottom: 4 }}>
          <strong>Completed milestones:</strong> {c.completed_milestones.map(m => m.title).join(', ')}
        </p>
      )}
      {c.next_milestone && (
        <p style={{ fontSize: 11, color: '#374151' }}>
          <strong>Next step:</strong> {c.next_milestone.title}{c.next_milestone.target_date ? ` (target ${c.next_milestone.target_date})` : ''}
        </p>
      )}
    </div>
  );
};

export default SeasonReportPrint;
